<template>
    <v-container fluid class="pa-3" v-if="sessionActive">

        <v-alert v-if="!hasPermission(minPermission)"
                 class="body-2"
                 type="error"
        >
            Your user account doesn't have sufficient permissions to use this feature
        </v-alert>

        <v-card flat v-if="hasPermission(minPermission)">

            <v-container fluid>

                <v-row dense>

                    <v-spacer></v-spacer>
                    <v-col cols="1" class="text-right">
                        <!-- Reload -->
                        <v-tooltip top color="primary">
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    @click="getHistory()"
                                    v-on="on"
                                    icon>
                                    <v-icon>mdi-refresh</v-icon>
                                </v-btn>
                            </template>
                            <span>Reload data</span>
                        </v-tooltip>
                    </v-col>

                </v-row>
            </v-container>

        </v-card>

        <v-row v-if="hasPermission(minPermission)">

            <v-col cols="12" class="pt-0">

                <v-data-table
                    :headers="headers"
                    :items="history.data"
                    :server-items-length="history.total"
                    :loading="historyLoading"
                    :footer-props.sync="footerProps"
                    :options.sync="tableOptions"
                    @update:options="getHistory"
                    @toggle-select-all="selectAll"
                    :footer-props="{itemsPerPageOptions: [10,25,50,100]}"
                >
                    <template v-slot:body="{ items }">
                        <tbody>

                        <!-- NO DATA INFO -->
                        <tr v-if="items.length===0 && !historyLoading">
                            <td colspan="99">
                                <v-alert
                                    color="primary"
                                    class="mt-4 body-2"
                                    icon="mdi-information">
                                    No redemptions found
                                </v-alert>
                            </td>
                        </tr>

                        <tr v-for="item in items" :key="item.id">

                            <!-- REDEMPTION ID -->
                            <td class="text-center">

                                <v-tooltip top v-if="hasPermission('view projects')">
                                    <template v-slot:activator="{ on }">
                                        <v-btn icon @click="copy(item)">
                                            <v-icon v-on="on" color="grey lighten-1">mdi-information-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <div class="padding-2">
                                        Redemption ID: {{ item.id }}
                                        <div v-if="item.partner_data">
                                            Reward partner data:
                                            <json :data="item.partner_data"/>
                                        </div>
                                        Click icon to copy to clipboard
                                    </div>
                                </v-tooltip>
                            </td>

                            <!-- COST OF REDEMPTION -->
                            <td>
                                <div v-if="item.reward">{{ item.reward.name }}</div>
                                <div v-else>
                                    <v-tooltip top color="primary" v-if="hasPermission('view projects')">
                                        <template v-slot:activator="{ on }">
                                            <v-icon small>mdi-circle-off-outline</v-icon>
                                        </template>
                                        <span>No reward associated to this redemptions</span>
                                    </v-tooltip>
                                </div>
                            </td>

                            <!-- COST -->
                            <td class="text-right">
                                {{ item.cost.toLocaleString(undefined, {minimumFractionDigits: 2}) }} {{ item.cost_currency }}
                            </td>

                            <!-- APPROVAL -->
                            <td>
                                <v-tooltip top color="primary" v-if="hasPermission('view projects')">
                                    <template v-slot:activator="{ on }">

                                        <v-chip v-on="on" label :color="rejectedColor" v-if="item.approved === false">
                                            <v-icon left>mdi-close</v-icon>
                                            Rejected
                                        </v-chip>

                                        <v-chip v-on="on" label :color="approvedColor" v-if="item.approved === true">
                                            <v-icon left>mdi-check</v-icon>
                                            Approved
                                        </v-chip>

                                        <v-chip v-on="on" label :color="reviewColor" v-if="item.approved === null">
                                            <v-icon left>mdi-eye</v-icon>
                                            Review
                                        </v-chip>

                                    </template>
                                    This redemption was audited and approved by
                                    {{ item.approved_by.name }}
                                </v-tooltip>
                            </td>

                            <!-- STATUS -->
                            <td class="text-left">
                                <v-chip label :color="getStatusColor(item)" v-show="item.partner_status"
                                        v-if="item.processed === true && item.failed === false && item.approved === true">
                                    {{ item.partner_status }}
                                </v-chip>

                                <v-chip color="red darken-2"
                                        v-if="item.processed === true && item.failed === true && item.approved === true">
                                    <v-icon left>mdi-close</v-icon>
                                    FAILED
                                </v-chip>

                                <v-chip label color="grey darken-2" v-if="item.processed === false && item.approved === true">
                                    <v-icon left>mdi-timer-sand</v-icon>
                                    QUEUED
                                </v-chip>
                            </td>

                            <!-- PANELIST -->
                            <td class="text-no-wrap text-center">
                                <v-tooltip top color="primary" v-if="hasPermission('view projects')">
                                    <template v-slot:activator="{ on }">
                                        <v-btn v-on="on"
                                               fab
                                               class="elevation-0"
                                               x-small
                                               @click="panelistClicked(item.panelist_id)"
                                               color="primary">
                                            <v-icon small>mdi-card-account-details-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <span>View panelist dashboard</span>
                                </v-tooltip>
                            </td>

                            <!-- WHEN -->
                            <td>
                                {{ toHumanFrom(item.created_at) }}
                            </td>
                        </tr>

                        </tbody>
                    </template>

                </v-data-table>

            </v-col>

        </v-row>

    </v-container>
</template>

<script>

import Json from 'vue-json-pretty';
import Store from '../store';
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import {timeDisplay} from "../../../components/mixins/timeDisplayMixin";
import Avatar from "../../../components/UI/Avatar";
import StatBoxNumerical from "../../../components/UI/StatBoxNumerical";
import {clipboardMixin} from "../../../components/mixins/clipboardMixin";

const vuexModule = 'Redemptions';

export default {

    mixins: [activeSessionChecker, timeDisplay, clipboardMixin],

    components: {
        StatBoxNumerical,
        MaterialHeader,
        Avatar,
        Json
    },

    data() {
        return {
            minPermission: 'view redemptions',
            selected: [],
            approvedColor: 'green darken-2',
            rejectedColor: 'red darken-2',
            reviewColor: 'orange',
            headers: [
                {text: '', align: 'center ', sortable: false, value: 'id', width: '40px'},
                {text: 'REWARD', align: 'left', sortable: false, width: '200px', value: 'reward.name'},
                {text: 'COST', align: 'right', sortable: false, value: 'cost', width: '120px'},
                {text: 'APPROVAL', sortable: false, value: 'approved', width: '50px'},
                {text: 'STATUS', align: 'left', sortable: false, width: '50px', value: 'processed'},
                {text: 'PANELIST', align: 'center', sortable: false, width: '50px', value: 'panelist_id'},
                {text: 'CALENDAR', sortable: false, align: 'left', width: '180px'},
            ],

            partnerStatusColors: {
                SUCCESS: 'green darken-2',
                COMPLETE: 'green darken-2',
                FAILED: 'red darken-2',
                PENDING: 'grey darken-2',
                UNCLAIMED: 'amber darken-3',
                RETURNED: 'red darken-2',
                ONHOLD: 'yellow darken-2',
                BLOCKED: 'red darken-2',
                REFUNDED: 'green darken-2',
                REVERSED: 'green darken-2',
            }

        }
    },
    computed: {

        ...mapState(vuexModule, [
            'history',
            'historyLoading',
            'historyTableOptions',
            'historyTableFooterProps',
        ]),

        tableOptions: {
            get() {
                return this.historyTableOptions;
            },
            set(options) {
                this.setHistoryTableOptions(options);
            }
        },

        footerProps: {
            get() {
                return this.historyTableFooterProps;
            },
            set(props) {
                this.setHistoryTableFooterProps(props);
            }
        },
    },

    methods: {
        ...mapActions(vuexModule, [
            'loadRedemptionHistory',
            'approveRedemptions',
            'rejectRedemptions'
        ]),

        ...mapMutations(vuexModule, [
            'setHistoryTableOptions'
        ]),

        getStatusColor(item) {
            if (!item.partner_status || !this.partnerStatusColors.hasOwnProperty(item.partner_status)) {
                return 'grey darken-2';
            }
            return this.partnerStatusColors[item.partner_status];
        },

        selectAll(config) {
            if (config.value) {
                this.selected = this.history.data.map(redemption => redemption.id);
            } else {
                this.selected = [];
            }
        },

        getHistory() {

            const config = {
                page: this.historyTableOptions.page,
                itemsPerPage: this.historyTableOptions.itemsPerPage
            };

            this.loadRedemptionHistory(config)
                .then(() => {
                })
                .catch(error => {
                    Bus.$emit('flash-message', {
                        type: 'error',
                        message: 'OOPS! Redemption approvals failed to load!'
                    });
                });
        },

        panelistClicked(panelistId) {
            this.$router.push('/spa/panelistmanager/edit/' + panelistId);
        },

        copy(item) {
            this.copyToClipboard(JSON.stringify(item.partner_data));
        }
    },

    watch: {
        $route(to, from) {
            if (to.name === 'redemptions') {
                this.getHistory();
            }
        }
    },

    mounted() {
        this.getHistory();
    },

    created() {
        if (!this.$store.state[vuexModule]) {
            this.$store.registerModule(vuexModule, Store);
        }
    },
}

</script>
